import React, { forwardRef, ReactNode } from 'react';

type SizeVariant = 'md' | 'lg';
type WeightVariant = 'regular' | 'semibold';
type Variant = 'default' | 'Filled-Darker' | 'Filled-Lighter';

interface DGATextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    label?: string | React.ReactNode;
    labelClassName?: string;
    containerClassName?: string;
    textareaWrapperClassName?: string;
    error?: boolean;
    errorMessage?: boolean | React.ReactNode;
    errorClassName?: string;
    description?: string;
    descriptionClassName?: string;
    textareaClassName?: string;
    required?: boolean;
    requiredIndicator?: ReactNode;
    resize?: 'none' | 'vertical' | 'horizontal' | 'both';
    TextAreaSize?: SizeVariant;
    LabelSize?: SizeVariant;
    LabelWeight?: WeightVariant;
    variant?: Variant;
}

/**
 * TextArea component to render a styled and accessible textarea with label, validation, variants, and RTL support.
 *
 * @param {string} id - Unique identifier for the textarea field.
 * @param {string} name - Name attribute for the textarea element.
 * @param {string} [placeholder] - Placeholder text for the textarea.
 * @param {string} [label] - Optional label displayed above the textarea.
 * @param {string} [value] - The current value of the textarea.
 * @param {(e: React.ChangeEvent<HTMLTextAreaElement>) => void} [onChange] - Change handler for the textarea.
 * @param {(e: React.FocusEvent<HTMLTextAreaElement>) => void} [onBlur] - Blur handler for the textarea.
 * @param {boolean} [required=false] - Whether the field is required.
 * @param {React.ReactNode} [requiredIndicator] - Custom React node to render as the required asterisk. Defaults to a red "*".
 * @param {boolean} [error] - Whether the field has an error state.
 * @param {boolean | React.ReactNode} [errorMessage] - Optional error message to display below the textarea.
 * @param {number} [rows=3] - Number of visible text lines for the textarea.
 * @param {'none' | 'vertical' | 'horizontal' | 'both'} [resize='vertical'] - Controls textarea resize behavior.
 * @param {SizeVariant} [TextAreaSize='lg'] - Size variant for the textarea.
 * @param {SizeVariant} [LabelSize='md'] - Size variant for the label.
 * @param {WeightVariant} [LabelWeight='regular'] - Weight variant for the label.
 * @param {Variant} [variant='default'] - Style variant for the textarea.
 * @param {string} [dir] - Directionality ("ltr" or "rtl"), for layout and text alignment.
 */
const DGATextArea = forwardRef<HTMLTextAreaElement, DGATextAreaProps>(({
    label,
    labelClassName = '',
    containerClassName = '',
    textareaWrapperClassName = '',
    error,
    errorMessage,
    errorClassName = '',
    description,
    descriptionClassName = '',
    textareaClassName = '',
    required = false,
    requiredIndicator,
    resize = 'vertical',
    rows = 3,
    TextAreaSize = 'lg',
    LabelSize = 'md',
    LabelWeight = 'regular',
    variant = 'default',
    id,
    name,
    ...textareaProps
}, ref) => {
    const textareaId = id || `textarea-${name}` || `textarea-${Math.random().toString(36).substr(2, 9)}`;

    const LabelSizeMap = {
        md: 'text-sm',
        lg: 'text-base',
        regular: 'font-normal',
        semibold: 'font-semibold',
    };

    // Resize classes mapping
    const resizeMap = {
        none: 'resize-none',
        vertical: 'resize-y',
        horizontal: 'resize-x',
        both: 'resize'
    };

    // Size variant configs
    const sizeMap = {
        md: {
            padding: 'px-2 py-1.5',
            font: 'text-sm',
            label: `${LabelSizeMap[LabelSize]} ${LabelSizeMap[LabelWeight]}`,
            gap: 'gap-1',
        },
        lg: {
            padding: 'px-4 py-3',
            font: 'text-base',
            label: `${LabelSizeMap[LabelSize]} ${LabelSizeMap[LabelWeight]}`,
            gap: 'gap-2',
        }
    };

    const size = sizeMap[TextAreaSize];

    // Helper to get wrapper classes by variant and state
    function getWrapperClasses(
        variant: Variant,
        error: boolean | undefined,
        disabled: boolean | undefined,
        textareaWrapperClassName: string
    ) {
        const base = `
            relative group cursor-text flex flex-row rtl:flex-row-reverse items-start w-full rounded
            transition-colors overflow-hidden
            before:absolute before:!-bottom-[1px] before:left-1/2 before:translate-x-[-50%]
            before:w-full before:!h-[3px] before:scale-x-0 before:origin-center before:transition-transform before:duration-300
            focus-within:before:scale-x-100
            ${textareaProps?.readOnly ? '!border-neutral-400 !bg-transparent' : ''}
        `.replace(/\s+/g, ' ').trim();

        const variants = {
            default: `
                border ${error ? '!border-[#B42318]' : 'border-neutral-400'}
                hover:border-neutral-700
                focus-within:${error ? '!border-[#B42318]' : 'border-neutral-400'}
                active:bg-neutral-100 active:border-neutral-400
                ${error ? 'before:bg-[#B42318]' : 'before:bg-neutral-950'}
                ${disabled ? '!border-neutral-300 !pointer-events-none' : ''}
            `.replace(/\s+/g, ' ').trim(),

            'Filled-Darker': `
                border ${error ? '!border-[#B42318]' : 'border-transparent'}
                hover:border-neutral-400
                bg-neutral-100
                active:bg-neutral-100
                active:!border-neutral-400
                focus-within:bg-transparent
                ${error ? 'focus-within:!border-[#B42318]' : 'focus-within:!border-neutral-400'}
                ${error ? 'before:bg-[#B42318]' : 'before:bg-neutral-950'}
                ${disabled || textareaProps?.readOnly ? '!border-neutral-300 !pointer-events-none !bg-transparent' : ''}
            `.replace(/\s+/g, ' ').trim(),

            'Filled-Lighter': `
                border ${error ? '!border-[#B42318]' : 'border-transparent'}
                hover:border-neutral-400
                bg-transparent
                active:bg-neutral-100
                active:!border-neutral-300
                focus-within:bg-transparent
                ${error ? 'focus-within:!border-[#B42318]' : 'focus-within:!border-neutral-400'}
                ${error ? 'before:bg-[#B42318]' : 'before:bg-neutral-950'}
                ${disabled || textareaProps?.readOnly ? '!border-neutral-300 !pointer-events-none !bg-transparent' : ''}
            `.replace(/\s+/g, ' ').trim(),
        };

        return `${base} ${variants[variant]} ${textareaWrapperClassName}`.trim();
    }

    const wrapperClasses = getWrapperClasses(variant, error, textareaProps.disabled, textareaWrapperClassName);

    const textareaBase = `
        w-full bg-transparent text-neutral-700 group-hover:text-primary-1000 outline-none border-none text-left rtl:!text-right
        placeholder:!text-neutral-500 transition-colors flex-1 ${size.font} ${resizeMap[resize]}
        ${textareaProps.readOnly ? '!text-primary-1000 !pointer-events-none' : ''}
        ${textareaProps.disabled ? '!text-neutral-400 placeholder:!text-neutral-400 !cursor-not-allowed' : ''}
        ${size.padding} ${size.gap}
    `.replace(/\s+/g, ' ').trim();

    const labelBase = `${textareaProps.disabled ? 'text-neutral-400' : 'text-primary-1000'} ${size.label}`.trim();
    const errorBase = `text-sm text-red-600`.trim();
    const descriptionBase = `text-sm text-gray-500`.trim();
    const containerBase = `peer flex flex-col items-start w-full gap-2`.trim();

    const finalTextAreaClasses = `${textareaBase} ${textareaClassName}`.trim();
    const finalLabelClasses = `${labelBase} ${labelClassName}`.trim();
    const finalErrorClasses = `${errorBase} ${errorClassName}`.trim();
    const finalDescriptionClasses = `${descriptionBase} ${descriptionClassName}`.trim();
    const finalContainerClasses = `${textareaProps?.readOnly ? '!pointer-events-none' : ''} ${containerBase} ${containerClassName}`.trim();

    const handleFocusOnTextAreaField = () => {
        const textareaElement = document?.getElementById?.(textareaId);
        if (textareaElement) {
            textareaElement.focus();
        }
    };

    return (
        <div className={finalContainerClasses} dir="auto">
            {label && (
                <label htmlFor={textareaId} className={finalLabelClasses}>
                    {label}
                    {required && (requiredIndicator || <span className={`${textareaProps.disabled ? 'text-neutral-400' : 'text-[#B42318]'} ltr:ml-1 rtl:mr-1`}>*</span>)}
                </label>
            )}

            <div onClick={handleFocusOnTextAreaField} className={wrapperClasses}>
                <textarea
                    ref={ref}
                    id={textareaId}
                    name={name}
                    required={required}
                    rows={rows}
                    className={finalTextAreaClasses}
                    {...textareaProps}
                />
            </div>

            {description && !error && (
                <p className={finalDescriptionClasses}>{description}</p>
            )}

            {error && (
                <p className={finalErrorClasses} role="alert">{errorMessage}</p>
            )}
        </div>
    );
});

DGATextArea.displayName = 'DGATextArea';
export default DGATextArea;